import React, { useState } from 'react';
import {
  ChevronDown, MapPin, Mail, Phone, Clock, ShieldCheck, Heart, Leaf, Truck, Gift, Star, Sparkles, RotateCcw, Award
} from 'lucide-react';
import { useSiteLayout } from '../../context/SiteLayoutContext';
import SmartLink from '../SmartLink/SmartLink';
import './Footer.css';

const HIGHLIGHT_ICONS = {
  leaf: Leaf, heart: Heart, shield: ShieldCheck, truck: Truck, gift: Gift,
  star: Star, sparkles: Sparkles, refresh: RotateCcw, award: Award
};

// Simple brand icons (this lucide version has no brand logos).
const SOCIAL_ICONS = {
  instagram: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13.5 21v-7.5h2.6l.4-3h-3V8.6c0-.9.3-1.5 1.6-1.5h1.6V4.4c-.3 0-1.2-.1-2.3-.1-2.3 0-3.9 1.4-3.9 4v2.2H8v3h2.5V21h3z" />
    </svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4a2.5 2.5 0 0 0-1.8 1.8C2 8.8 2 12 2 12s0 3.2.4 4.8a2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8c.4-1.6.4-4.8.4-4.8s0-3.2-.4-4.8zM10 15V9l5.2 3L10 15z" />
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.2-.4.7-1.2.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.1 5.1 0 0 0 1.1 2.7 11.7 11.7 0 0 0 4.5 4c1.7.7 2.3.8 3.2.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3z" />
    </svg>
  ),
  pinterest: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.2-5s-.3-.6-.3-1.5c0-1.4.8-2.4 1.8-2.4.9 0 1.3.6 1.3 1.4 0 .9-.6 2.2-.8 3.4-.2 1 .5 1.8 1.5 1.8 1.8 0 3.2-1.9 3.2-4.6 0-2.4-1.7-4.1-4.2-4.1-2.9 0-4.5 2.1-4.5 4.4 0 .9.3 1.8.8 2.3.1.1.1.2.1.3l-.3 1.2c0 .2-.2.2-.4.1-1.3-.6-2.1-2.5-2.1-4 0-3.3 2.4-6.3 6.9-6.3 3.6 0 6.4 2.6 6.4 6 0 3.6-2.3 6.5-5.4 6.5-1.1 0-2.1-.6-2.4-1.2l-.7 2.5c-.2.9-.9 2.1-1.3 2.8A10 10 0 1 0 12 2z" />
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.8 3h3.1l-6.8 7.7L22 21h-6.2l-4.9-6.3L5.3 21H2.2l7.2-8.3L2 3h6.4l4.4 5.8L17.8 3zm-1.1 16.2h1.7L7.4 4.7H5.6l11.1 14.5z" />
    </svg>
  )
};

const SOCIAL_NAMES = { instagram: 'Instagram', facebook: 'Facebook', youtube: 'YouTube', whatsapp: 'WhatsApp', pinterest: 'Pinterest', x: 'X (Twitter)' };

// Footer — everything here is edited in Admin → Customize → Footer
const Footer = () => {
  const [openSection, setOpenSection] = useState(null);
  const { layout } = useSiteLayout();
  const content = layout?.footer;
  const headerLogo = layout?.header?.logo;

  // Not loaded yet (first visit): the footer sits below the fold, so just wait.
  if (!content) return <footer className="site-footer" aria-busy="true" />;

  const toggleSection = (sec) => {
    setOpenSection(prev => (prev === sec ? null : sec));
  };

  const columns = content.columns || [];
  const highlights = content.showHighlights ? content.highlights || [] : [];
  const logo = content.logo || headerLogo || '/images/sha-botique-logo.png';
  const phoneHref = content.phone ? `tel:${content.phone.replace(/[^\d+]/g, '')}` : null;

  const footerStyle = {
    '--color-surface': content.bgColor,
    '--color-text-muted': content.textColor,
    '--color-primary': content.headingColor,
    '--color-primary-light': content.stripBgColor,
    '--footer-cols': columns.length ? `2fr repeat(${columns.length}, 1fr)` : '1fr',
    '--highlight-count': Math.max(highlights.length, 1)
  };

  return (
    <footer className="site-footer" style={footerStyle}>
      {/* Brand Ethos Strip */}
      {highlights.length > 0 && (
        <div className="footer-ethos-strip">
          <div className="ethos-container">
            {highlights.map(item => {
              const Icon = HIGHLIGHT_ICONS[item.icon] || Leaf;
              return (
                <div key={item.id} className="ethos-item">
                  <Icon size={20} className="ethos-icon" />
                  <div>
                    <p className="ethos-title">{item.title}</p>
                    {item.text && <p>{item.text}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Footer Links */}
      <div className="footer-main-container">
        {/* Brand & Story Column */}
        <div className="footer-col brand-col">
          <div className="footer-brand-logo">
            <img src={logo} alt={layout?.header?.logoAlt || 'ZIDA'} className="footer-logo-img" />
          </div>
          {content.about && <p className="footer-bio">{content.about}</p>}
          <div className="footer-contact-snippets">
            {content.address && (
              <div className="snippet-item">
                <MapPin size={15} className="snippet-icon" />
                <span>{content.address}</span>
              </div>
            )}
            {content.email && (
              <div className="snippet-item">
                <Mail size={15} className="snippet-icon" />
                <a href={`mailto:${content.email}`}>{content.email}</a>
              </div>
            )}
            {content.phone && (
              <div className="snippet-item">
                <Phone size={15} className="snippet-icon" />
                <a href={phoneHref}>{content.phone}</a>
              </div>
            )}
            {content.hours && (
              <div className="snippet-item">
                <Clock size={15} className="snippet-icon" />
                <span>{content.hours}</span>
              </div>
            )}
          </div>

          {content.socialLinks?.length > 0 && (
            <div className="footer-social-row">
              {content.socialLinks.map(social => (
                <a
                  key={social.id}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social-link"
                  aria-label={SOCIAL_NAMES[social.platform] || social.platform}
                  title={SOCIAL_NAMES[social.platform] || social.platform}
                >
                  {SOCIAL_ICONS[social.platform]}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Link Columns */}
        {columns.map(column => (
          <div key={column.id} className={`footer-col accordion-col ${openSection === column.id ? 'open' : ''}`}>
            <div className="footer-col-header" onClick={() => toggleSection(column.id)}>
              <h2>{column.title}</h2>
              <ChevronDown size={16} className="accordion-chevron" />
            </div>
            <ul className="footer-link-list">
              {column.links.map(item => (
                <li key={item.id}>
                  <SmartLink to={item.link} fallbackTag="span">{item.label}</SmartLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Bottom Copyright & Disclaimer */}
      {(content.copyright || content.disclaimer) && (
        <div className="footer-bottom-bar">
          <div className="bottom-bar-inner">
            {content.copyright && <p className="copyright-text">{content.copyright}</p>}
            {content.disclaimer && <p className="demo-disclaimer-text">{content.disclaimer}</p>}
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
